import React, { useState, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faQuestionCircle,
  faPlus,
  faFileImport,
  faFileCsv,
  faEdit,
  faTrash,
  faChevronLeft,
  faChevronRight,
  faShieldAlt,
  faRefresh,
} from '@fortawesome/free-solid-svg-icons';
import { streetlearnApi } from '../api';
import { showToast } from '../../components/toast';
import { API_URL } from '../../config';
import QuestionModal from './QuestionModal';
import ImportModal from './ImportModal';
import '../streetlearn.css';

const QuestionBank = () => {
  const [questions, setQuestions] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [limit] = useState(25);

  // Filters
  const [search, setSearch] = useState('');
  const [levelFilter, setLevelFilter] = useState('');
  const [useInFilter, setUseInFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [noLessonFilter, setNoLessonFilter] = useState(false);

  // Pool summary
  const [poolSummary, setPoolSummary] = useState([]);
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [showSummaryModal, setShowSummaryModal] = useState(false);

  // Selection for bulk
  const [selectedIds, setSelectedIds] = useState([]);

  // Modals
  const [showQuestionModal, setShowQuestionModal] = useState(false);
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [showImportModal, setShowImportModal] = useState(false);

  // Delete confirmation
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const fetchSummary = useCallback(async () => {
    try {
      setLoadingSummary(true);
      const data = await streetlearnApi.getQuestionPoolSummary();
      setPoolSummary(data || []);
    } catch (err) {
      console.error('Failed to load pool summary:', err);
    } finally {
      setLoadingSummary(false);
    }
  }, []);

  const fetchQuestions = useCallback(async () => {
    try {
      setLoading(true);
      const data = await streetlearnApi.getQuestions({
        page,
        limit,
        q: search.trim() || undefined,
        level_id: levelFilter || undefined,
        use_in: useInFilter || undefined,
        status: statusFilter || undefined,
        no_lesson: noLessonFilter ? '1' : undefined,
      });
      setQuestions(data.items || []);
      setTotal(data.total || 0);
      setSelectedIds([]);
    } catch (err) {
      showToast.error(err.response?.data?.message || 'Failed to load questions');
    } finally {
      setLoading(false);
    }
  }, [page, limit, search, levelFilter, useInFilter, statusFilter, noLessonFilter]);

  useEffect(() => {
    fetchQuestions();
  }, [fetchQuestions]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  // Bulk actions
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedIds(questions.map((q) => q.id));
    } else {
      setSelectedIds([]);
    }
  };

  const handleSelectOne = (id) => {
    if (selectedIds.includes(id)) {
      setSelectedIds(selectedIds.filter((item) => item !== id));
    } else {
      setSelectedIds([...selectedIds, id]);
    }
  };

  const handleBulkStatus = async (newStatus) => {
    if (!selectedIds.length) return;
    try {
      await streetlearnApi.bulkUpdateQuestionStatus({
        question_ids: selectedIds,
        status: newStatus,
      });
      showToast.success(`Updated ${selectedIds.length} question(s) to ${newStatus}`);
      fetchQuestions();
      fetchSummary();
    } catch (err) {
      showToast.error(err.response?.data?.message || 'Bulk update failed');
    }
  };

  // Delete
  const confirmDelete = async (force = false) => {
    if (!deleteTarget) return;
    try {
      setDeleting(true);
      await streetlearnApi.deleteQuestion(deleteTarget.id, force);
      showToast.success('Question deleted successfully');
      setDeleteTarget(null);
      fetchQuestions();
      fetchSummary();
    } catch (err) {
      const errData = err.response?.data;
      if (err.response?.status === 409 && !force) {
        if (
          window.confirm(
            `${errData?.message || 'Warning: lesson pool will drop below required minimum.'}\n\nDo you want to FORCE delete anyway?`
          )
        ) {
          confirmDelete(true);
          return;
        }
      } else {
        showToast.error(errData?.message || 'Failed to delete question');
      }
    } finally {
      setDeleting(false);
    }
  };

  // Export
  const handleExportCsv = () => {
    const token = localStorage.getItem('authToken');
    const params = new URLSearchParams();
    if (search.trim()) params.append('q', search.trim());
    if (levelFilter) params.append('level_id', levelFilter);
    if (useInFilter) params.append('use_in', useInFilter);
    if (statusFilter) params.append('status', statusFilter);
    if (noLessonFilter) params.append('no_lesson', '1');
    if (token) params.append('token', token);

    window.open(`${API_URL}streetlearn/questions/export?${params.toString()}`, '_blank');
  };

  const totalPages = Math.ceil(total / limit) || 1;
  const start = total === 0 ? 0 : (page - 1) * limit + 1;
  const end = Math.min(page * limit, total);

  // Aggregate stats across pool
  const totalInPool = poolSummary.reduce((acc, row) => acc + (row.total_questions || 0), 0);
  const totalPublished = poolSummary.reduce((acc, row) => acc + (row.published_count || 0), 0);
  const totalDraft = poolSummary.reduce((acc, row) => acc + (row.draft_count || 0), 0);
  const levelsTightOrShort = poolSummary.filter((row) => row.pool_health !== 'ok').length;

  return (
    <div className="mt-5 streetlearn-container">
      {/* Page Header (Subscriber layout) */}
      <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap">
        <div>
          <h4 className="panel-title m-0">
            <FontAwesomeIcon icon={faQuestionCircle} className="me-2 text-primary" />
            StreetLearn Question Bank
          </h4>
        </div>
        <div className="d-flex align-items-center flex-wrap">
          <button className="btn btn-default btn-sm mx-1" onClick={fetchQuestions} title="Refresh">
            <FontAwesomeIcon icon={faRefresh} spin={loading} /> Refresh
          </button>
          <button className="btn btn-default btn-sm mx-1" onClick={handleExportCsv}>
            <FontAwesomeIcon icon={faFileCsv} /> Export CSV
          </button>
          <button className="btn btn-default btn-sm mx-1" onClick={() => setShowImportModal(true)}>
            <FontAwesomeIcon icon={faFileImport} /> Import CSV
          </button>
          <button
            className="btn btn-primary btn-sm mx-1"
            onClick={() => {
              setCurrentQuestion(null);
              setShowQuestionModal(true);
            }}
          >
            <FontAwesomeIcon icon={faPlus} /> Add Question
          </button>

          {total > 0 && (
            <div className="d-inline-flex align-items-center ms-2">
              <span style={{ fontSize: '0.8rem', whiteSpace: 'nowrap' }} className="me-2">
                {start} to {end} of {total}
              </span>
              <button
                className="btn btn-default btn-sm"
                title="Previous"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                <FontAwesomeIcon icon={faChevronLeft} />
              </button>
              &nbsp;
              <button
                className="btn btn-default btn-sm"
                title="Next"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              >
                <FontAwesomeIcon icon={faChevronRight} />
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Pool Health Summary Widgets */}
      <div className="row mb-3">
        <div className="col-xl-3 col-lg-3 col-md-6 col-sm-6 col-6 mb-3">
          <div
            className="widget widget-stats bg-gradient-dark-blue inverse-mode"
            style={{
              background: 'linear-gradient(135deg, #1b355a 0%, #28548a 100%)',
              minHeight: '96px',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              padding: '12px 14px',
              borderRadius: '6px',
              marginBottom: '0',
            }}
          >
            <div
              className="widget-stats-title"
              style={{
                fontSize: '15px',
                fontWeight: '600',
                color: 'rgba(255,255,255,0.95)',
                lineHeight: '1.2',
                textAlign: 'left',
              }}
            >
              Total Questions
            </div>
            <div
              className="widget-stats-value"
              style={{
                fontSize: '32px',
                fontWeight: 'bold',
                lineHeight: '1',
                textAlign: 'right',
              }}
            >
              {totalInPool || total}
            </div>
          </div>
        </div>

        <div className="col-xl-3 col-lg-3 col-md-6 col-sm-6 col-6 mb-3">
          <div
            className="widget widget-stats bg-gradient-green inverse-mode"
            style={{
              minHeight: '96px',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              padding: '12px 14px',
              borderRadius: '6px',
              marginBottom: '0',
            }}
          >
            <div
              className="widget-stats-title"
              style={{
                fontSize: '15px',
                fontWeight: '600',
                color: 'rgba(255,255,255,0.95)',
                lineHeight: '1.2',
                textAlign: 'left',
              }}
            >
              Published Questions
            </div>
            <div
              className="widget-stats-value"
              style={{
                fontSize: '32px',
                fontWeight: 'bold',
                lineHeight: '1',
                textAlign: 'right',
              }}
            >
              {totalPublished}
            </div>
          </div>
        </div>

        <div className="col-xl-3 col-lg-3 col-md-6 col-sm-6 col-6 mb-3">
          <div
            className="widget widget-stats bg-gradient-orange inverse-mode"
            style={{
              minHeight: '96px',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              padding: '12px 14px',
              borderRadius: '6px',
              marginBottom: '0',
            }}
          >
            <div
              className="widget-stats-title"
              style={{
                fontSize: '15px',
                fontWeight: '600',
                color: 'rgba(255,255,255,0.95)',
                lineHeight: '1.2',
                textAlign: 'left',
              }}
            >
              Draft / Staged
            </div>
            <div
              className="widget-stats-value"
              style={{
                fontSize: '32px',
                fontWeight: 'bold',
                lineHeight: '1',
                textAlign: 'right',
              }}
            >
              {totalDraft}
            </div>
          </div>
        </div>

        <div className="col-xl-3 col-lg-3 col-md-6 col-sm-6 col-6 mb-3">
          <div
            className="widget widget-stats bg-gradient-purple inverse-mode"
            style={{
              minHeight: '96px',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              padding: '12px 14px',
              borderRadius: '6px',
              marginBottom: '0',
            }}
          >
            <div className="d-flex justify-content-between align-items-center">
              <div
                className="widget-stats-title"
                style={{
                  fontSize: '15px',
                  fontWeight: '600',
                  color: 'rgba(255,255,255,0.95)',
                  lineHeight: '1.2',
                  textAlign: 'left',
                }}
              >
                Pool Health Matrix
              </div>
              <button
                className="btn btn-default btn-xs text-dark"
                onClick={() => setShowSummaryModal(true)}
                style={{ fontSize: '10px', padding: '2px 6px' }}
              >
                <FontAwesomeIcon icon={faShieldAlt} className="me-1" /> Matrix
              </button>
            </div>
            <div
              className="widget-stats-value"
              style={{
                fontSize: '22px',
                fontWeight: 'bold',
                lineHeight: '1',
                textAlign: 'right',
              }}
            >
              {levelsTightOrShort === 0 ? 'All Healthy' : `${levelsTightOrShort} Need Action`}
            </div>
          </div>
        </div>
      </div>

      {/* Bulk Action Bar */}
      {selectedIds.length > 0 && (
        <div className="alert alert-info py-2 px-3 d-flex justify-content-between align-items-center mb-3">
          <span className="small">
            Selected <strong>{selectedIds.length}</strong> question(s)
          </span>
          <div className="btn-group btn-group-xs">
            <button
              className="btn btn-default btn-xs"
              onClick={() => handleBulkStatus('published')}
            >
              Publish Selected
            </button>
            <button
              className="btn btn-default btn-xs"
              onClick={() => handleBulkStatus('draft')}
            >
              Set as Draft
            </button>
            <button
              className="btn btn-default btn-xs"
              onClick={() => setSelectedIds([])}
            >
              Deselect All
            </button>
          </div>
        </div>
      )}

      {/* Main Table Panel (Exact Subscriber Module UI) */}
      <div className="panel panel-default with-rounded-corner with-shadow mb-3">
        <div className="panel-body p-0">
          <div className="table-responsive">
            <table className="table table-condensed table-hover bg-white sticky-table m-0">
              <thead>
                <tr>
                  <th style={{ width: '40px', textAlign: 'center' }}>
                    <input
                      type="checkbox"
                      checked={questions.length > 0 && selectedIds.length === questions.length}
                      onChange={handleSelectAll}
                    />
                  </th>
                  <th>Question Stem (Text) &amp; Options</th>
                  <th style={{ width: '160px' }}>Level / Lesson</th>
                  <th style={{ width: '120px' }}>Type</th>
                  <th style={{ width: '110px' }}>Status</th>
                  <th style={{ width: '130px' }}>Stats</th>
                  <th style={{ width: '90px', textAlign: 'center' }}>Actions</th>
                </tr>
                {/* 🔎 In-Table Search Filter Row (Exact Subscriber Input & Select Style) */}
                <tr>
                  <th></th>
                  <th>
                    <input
                      type="text"
                      placeholder=""
                      value={search}
                      onChange={(e) => {
                        setSearch(e.target.value);
                        setPage(1);
                      }}
                      style={{
                        width: '100%',
                        padding: '5px',
                        fontSize: '12px',
                      }}
                    />
                  </th>
                  <th>
                    <select
                      value={levelFilter}
                      onChange={(e) => {
                        setLevelFilter(e.target.value);
                        setPage(1);
                      }}
                      style={{ width: '100%', padding: '5px', fontSize: '12px' }}
                    >
                      <option value="">--select--</option>
                      {Array.from({ length: 16 }, (_, i) => i + 1).map((lvl) => (
                        <option key={lvl} value={lvl}>
                          Level {lvl}
                        </option>
                      ))}
                    </select>
                  </th>
                  <th>
                    <select
                      value={useInFilter}
                      onChange={(e) => {
                        setUseInFilter(e.target.value);
                        setPage(1);
                      }}
                      style={{ width: '100%', padding: '5px', fontSize: '12px' }}
                    >
                      <option value="">--select--</option>
                      <option value="both">Both</option>
                      <option value="quiz">Quiz</option>
                      <option value="exam">Exam</option>
                    </select>
                  </th>
                  <th>
                    <select
                      value={statusFilter}
                      onChange={(e) => {
                        setStatusFilter(e.target.value);
                        setPage(1);
                      }}
                      style={{ width: '100%', padding: '5px', fontSize: '12px' }}
                    >
                      <option value="">--select--</option>
                      <option value="published">Published</option>
                      <option value="draft">Draft</option>
                    </select>
                  </th>
                  <th style={{ verticalAlign: 'middle' }}>
                    <label
                      className="m-0 small d-flex align-items-center"
                      style={{ cursor: 'pointer', fontSize: '11px', whiteSpace: 'nowrap' }}
                    >
                      <input
                        type="checkbox"
                        checked={noLessonFilter}
                        onChange={(e) => {
                          setNoLessonFilter(e.target.checked);
                          setPage(1);
                        }}
                        className="me-1"
                      />
                      Unassigned
                    </label>
                  </th>
                  <th style={{ textAlign: 'center' }}>
                    <button
                      type="button"
                      className="btn btn-default btn-xs"
                      title="Clear all filters"
                      onClick={() => {
                        setSearch('');
                        setLevelFilter('');
                        setUseInFilter('');
                        setStatusFilter('');
                        setNoLessonFilter(false);
                        setPage(1);
                      }}
                    >
                      Clear
                    </button>
                  </th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={7} className="text-center py-4">
                      <FontAwesomeIcon icon={faRefresh} spin className="me-2 text-primary" />
                      Loading questions...
                    </td>
                  </tr>
                ) : questions.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="text-center py-4 text-muted">
                      No questions found matching criteria.
                    </td>
                  </tr>
                ) : (
                  questions.map((q) => {
                    const opts = Array.isArray(q.options)
                      ? q.options
                      : typeof q.options === 'string'
                      ? JSON.parse(q.options || '[]')
                      : [];
                    return (
                      <tr key={q.id}>
                        <td style={{ textAlign: 'center' }}>
                          <input
                            type="checkbox"
                            checked={selectedIds.includes(q.id)}
                            onChange={() => handleSelectOne(q.id)}
                          />
                        </td>
                        <td>
                          <div className="font-bold text-dark mb-1">{q.stem}</div>
                          <div className="small text-muted">
                            {opts.map((opt, i) => (
                              <span
                                key={i}
                                className={`me-3 ${
                                  i === q.correct_index ? 'text-success font-bold' : ''
                                }`}
                              >
                                {String.fromCharCode(65 + i)}) {opt}
                                {i === q.correct_index && ' ✓'}
                              </span>
                            ))}
                          </div>
                        </td>
                        <td>
                          <span className="font-weight-bold text-primary">Level {q.level_id}</span>
                          {q.lesson ? (
                            <div className="text-muted small mt-1">
                              {q.lesson.code} - {q.lesson.title}
                            </div>
                          ) : (
                            <div className="text-warning small mt-1">Unassigned (Pool)</div>
                          )}
                        </td>
                        <td>
                          <span className="font-weight-bold text-dark text-capitalize">
                            {q.use_in === 'both' ? 'Quiz + Exam' : q.use_in}
                          </span>
                        </td>
                        <td>
                          {q.status === 'published' ? (
                            <span className="text-success font-weight-bold">Published</span>
                          ) : (
                            <span className="text-warning font-weight-bold">Draft</span>
                          )}
                        </td>
                        <td className="small text-muted">
                          <div>Attempts: {q.times_served || 0}</div>
                          <div>
                            Correct:{' '}
                            {q.times_served > 0
                              ? Math.round(((q.times_correct || 0) / q.times_served) * 100)
                              : 0}
                            %
                          </div>
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          <button
                            className="btn btn-default btn-xs mx-1"
                            title="Edit Question"
                            onClick={() => {
                              setCurrentQuestion(q);
                              setShowQuestionModal(true);
                            }}
                          >
                            <FontAwesomeIcon icon={faEdit} />
                          </button>
                          <button
                            className="btn btn-danger btn-xs mx-1"
                            title="Delete Question"
                            onClick={() => setDeleteTarget(q)}
                          >
                            <FontAwesomeIcon icon={faTrash} />
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Question Modal */}
      {showQuestionModal && (
        <QuestionModal
          show={showQuestionModal}
          question={currentQuestion}
          onHide={() => setShowQuestionModal(false)}
          onSuccess={() => {
            setShowQuestionModal(false);
            fetchQuestions();
            fetchSummary();
          }}
        />
      )}

      {/* Import Modal */}
      {showImportModal && (
        <ImportModal
          show={showImportModal}
          onHide={() => setShowImportModal(false)}
          onSuccess={() => {
            setShowImportModal(false);
            fetchQuestions();
            fetchSummary();
          }}
        />
      )}

      {/* Pool Summary Matrix Modal */}
      {showSummaryModal && (
        <div
          className="modal show d-block"
          style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1050 }}
        >
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header d-flex justify-content-between align-items-center">
                <h4 className="modal-title font-bold m-0">Question Pool Health Matrix (16 Levels)</h4>
                <button
                  type="button"
                  className="close"
                  style={{ border: 'none', background: 'transparent', fontSize: '24px', lineHeight: '1', cursor: 'pointer', padding: '0 5px' }}
                  onClick={() => setShowSummaryModal(false)}
                >
                  <span>&times;</span>
                </button>
              </div>
              <div className="modal-body p-0">
                <div className="table-responsive">
                  <table className="table table-striped table-bordered table-hover align-middle mb-0">
                    <thead>
                      <tr>
                        <th>Level</th>
                        <th>Total</th>
                        <th>Quiz Pool</th>
                        <th>Exam Pool</th>
                        <th>Required</th>
                        <th>Published</th>
                        <th>Draft</th>
                        <th>Health</th>
                      </tr>
                    </thead>
                    <tbody>
                      {poolSummary.map((p) => (
                        <tr key={p.level_id}>
                          <td>
                            <strong>Level {p.level_id}</strong>: {p.level_title}
                          </td>
                          <td>{p.total_questions}</td>
                          <td>{p.quiz_questions}</td>
                          <td>{p.exam_questions}</td>
                          <td>{p.exam_required_count}</td>
                          <td className="text-success">{p.published_count}</td>
                          <td className="text-warning">{p.draft_count}</td>
                          <td>
                            <span
                              className={`badge ${
                                p.pool_health === 'ok'
                                  ? 'badge-success'
                                  : p.pool_health === 'tight'
                                  ? 'badge-warning'
                                  : 'badge-danger'
                              }`}
                            >
                              {p.pool_health === 'ok' && 'Healthy (2x+)'}
                              {p.pool_health === 'tight' && 'Tight (<2x)'}
                              {p.pool_health === 'short' && 'Short (Deficit)'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-default btn-sm"
                  onClick={() => setShowSummaryModal(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteTarget && (
        <div
          className="modal show d-block"
          style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1050 }}
        >
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header d-flex justify-content-between align-items-center">
                <h4 className="modal-title font-bold m-0">Delete Question #{deleteTarget.id}</h4>
                <button
                  type="button"
                  className="close"
                  style={{ border: 'none', background: 'transparent', fontSize: '24px', lineHeight: '1', cursor: 'pointer', padding: '0 5px' }}
                  onClick={() => setDeleteTarget(null)}
                >
                  <span>&times;</span>
                </button>
              </div>
              <div className="modal-body">
                <p>Are you sure you want to delete this question?</p>
                <div className="alert alert-warning py-2 mb-0">
                  <strong>Question:</strong> {deleteTarget.stem}
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-default btn-sm"
                  onClick={() => setDeleteTarget(null)}
                  disabled={deleting}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-danger btn-sm"
                  onClick={() => confirmDelete(false)}
                  disabled={deleting}
                >
                  {deleting ? 'Deleting...' : 'Delete Question'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default QuestionBank;
